import {
	Dialog,
	Form,
	FormItem,
	Toolbar,
	renderReactDialog,
	useAlertDialog
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	DocumentItemViewModel,
	DocumentItemType,
	DocumentsErrorModel
} from "client/Api/Documents/SharePoint/common";
import { localize } from "client_legacy/ndaw/culture";
import { ReactElement, useCallback, useState } from "react";
import { TextInputWithExtension } from "../../Dialogs/TextInputWithExtension";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { getExtension, getFileNameWithoutExtension } from "client_legacy/Documents/path";
import { never } from "client_legacy/ndaw/errors";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	IRootValidation,
	InputValidationResult,
	InputValidationResultKind,
	convertServerErrors,
	createValidationConfiguration
} from "client/Validation";
import {
	RenameModel,
	renameFile,
	renameFolder
} from "client/Api/Documents/SharePoint/sharePointAction";
import { SharePointEvent, publish } from "client_legacy/Documents/SharePoint/documentEvents";
import { HttpRequestError } from "client/Api/httpClient";
import { isNotNil } from "client/Misc/utils";
import { ClientValidationErrorModel } from "client/Api/common";
import { inline } from "client/Validation/rules";
import * as namePolicy from "client_legacy/Documents/SharePoint/documentNamePolicy";
import { convertToLocationModel } from "client_legacy/Documents/SharePoint/listLocationModelProvider";

export type RenameDialogProps = {
	item: DocumentItemViewModel;
	onClose: () => void;
};

export const RenameDialog = (props: RenameDialogProps): ReactElement => {
	const {
		item,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [formData, setFormData] = useState<FormData>(
		(): FormData => convertToFormData(item)
	);

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [alertDialog, openAlertDialog] = useAlertDialog();

	const formHandler: IFormHandler<FormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const submitHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			const validation: IRootValidation<FormData> = formHandler.validation;
			try {

				const validationResult: InputValidationResult = await validation.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				const model: RenameModel = {
					documentId: item.id,
					location: convertToLocationModel(item.location),
					newDocumentName: (formData.name + formData.extension).trimEnd()
				};

				let newDocument: DocumentItemViewModel;

				switch (item.itemType) {
					case DocumentItemType.File:
						newDocument = await renameFile(model, globalCancellation);
						break;
					case DocumentItemType.Folder:
					case DocumentItemType.OneNoteNotebook:
						newDocument = await renameFolder(model, globalCancellation);
						break;
					default:
						never(item);
				}

				publish(
					SharePointEvent.Renamed,
					{ newDocument }
				);

				onClose();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				let handled = false;
				if (e instanceof HttpRequestError && e.statusCode === 400) {
					try {
						const response: DocumentsErrorModel = e.readJson<DocumentsErrorModel>();
						const fieldName: string = response.field;
						if (fieldName === "Name" && "message" in response && isNotNil(response.message)) {
							const errors: readonly ClientValidationErrorModel[] = [
								{
									propertyName: fieldName,
									errorMessage: response.message
								}
							];
							validation.setServerErrors(
								convertServerErrors(errors)
							);
							handled = true;
						} else if ("messageTitle" in response && isNotNil(response.messageTitle)) {
							await openAlertDialog({
								title: response.messageTitle,
								children: response.messageBody,
								confirmText: localize("_Close"),
								showCloseButton: true,
								trackingId: "sp-rename-error-dialog"
							});

							onClose();
							return;
						}
					} catch (e) {
						console.error(e);
					}
				}

				if (!handled) {
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}

				setIsBusy(false);
			}
		},
		[
			alertManager,
			formData.extension,
			formData.name,
			formHandler.validation,
			globalCancellation,
			isBusy,
			item,
			onClose,
			openAlertDialog
		]
	);

	if (alertDialog) {
		return alertDialog;
	}

	return (
		<Dialog
			trackingId="sp-rename-dialog"
			hasBusyIndicator={isBusy}
		>
			<Dialog.Header
				title={localize("_Rename")}
				onClose={onClose}
			/>

			<Dialog.Body>
				<Form onSubmit={submitHandler}>
					<FormItem>
						<TextInputWithExtension
							attachable={formHandler.for("name")}
							fileExtension={formData.extension}
							placeholder={localize("_EnterNewName")}
							isMobile={isMobile}
						/>
					</FormItem>
				</Form>
			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						trackingId="rename"
						text={localize("_Rename")}
						onClick={submitHandler}
					/>

					<Toolbar.Button
						kind="secondary"
						trackingId="cancel"
						text={localize("_Cancel")}
						onClick={onClose}
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
RenameDialog.displayName = "RenameDialog";

type FormData = {
	extension: string;
	name: string;
};

const validationConfiguration = createValidationConfiguration<FormData>();

validationConfiguration.forScalar("name")
	.add(
		inline(
			async (value): Promise<InputValidationResult> => {
				const illegalCharacters: string[] = await namePolicy.getIllegalCharacters();
				if (!namePolicy.isValid(value, illegalCharacters)) {
					const illegalCharactersString: string = illegalCharacters.join(" ");
					return {
						kind: InputValidationResultKind.Invalid,
						errorMessage: localize("_ContainsIllegalCharacters")
							.replace("{0}", illegalCharactersString)
					};
				}

				if (value.indexOf(" ") === 0) {
					return {
						kind: InputValidationResultKind.Invalid,
						errorMessage: localize("_NameCantStartWithASpace")
					};
				}

				// trim the name because file/folder names with only spaces/tabs are not allowed.
				if (value.trim() === "") {
					return {
						kind: InputValidationResultKind.Invalid,
						errorMessage: localize("_FillInFileName")
					};
				}

				return {
					kind: InputValidationResultKind.Valid
				};
			}
		)
	);

function convertToFormData(item: DocumentItemViewModel): FormData {
	let name: string = item.name;
	let extension = "";
	switch (item.itemType) {
		case DocumentItemType.Folder:
		case DocumentItemType.OneNoteNotebook:
			break;
		case DocumentItemType.File:
			extension = getExtension(name);
			if (extension !== "") {
				name = getFileNameWithoutExtension(name);
			}
			break;
		default:
			never(item);
	}

	return {
		name: name,
		extension
	};
}

export function openRenameDialog(
	props: Omit<RenameDialogProps, "onClose">
): void {
	return renderReactDialog(
		(onClose): ReactElement => (
			<RenameDialog
				{...props}
				onClose={onClose}
			/>
		)
	);
}
